import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router";
import { format, parseISO } from "date-fns";
import { Check } from "lucide-react";
import { usePlatformCapabilities } from "@shared-core/capabilities/context";
import { useSupplierPortalAdapter } from "@shared-core/data/SupplierPortalAdapterContext";
import type { SupplierAssessment } from "@shared-core/data/supplier-portal-adapter";
import { extractFieldErrors } from "@shared-core/data/validation";
import { useLoad } from "@shared-core/domain/useLoad";
import { mutationErrorMessage } from "@shared-core/domain/mutation-error-message";
import {
  ASSESSMENT_OUTCOME_LABEL,
  FORM_WHY,
  missingItems,
  stepsFor,
  type AssessmentAnswers,
  type FileAnswer,
  type MissingItem,
  type SectionId,
} from "@shared-core/domain/supplier-assessment";
import { paths } from "@shared-core/routing/routes";
import { Button } from "../../app/components/primitives/button";
import { Card } from "../../app/components/primitives/card";
import { cn } from "../../app/components/primitives/utils";
import { StatusPill } from "../../app/components/domain/StatusPill";
import { useCompanySummary } from "../shell/CompanySummaryContext";
import { Banner, EmptyState, LoadFailed, LoadingCard } from "../shell/states";
import { AssessmentFilesContext } from "./AssessmentControls";
import { AssessmentSectionBody } from "./AssessmentSections";
import {
  useAssessmentAutosave,
  type SaveStatus,
} from "./useAssessmentAutosave";

const day = (iso: string) => format(parseISO(iso), "d MMM yyyy");

/** The card the current section sits in: moving between sections brings it back into view. */
const SECTION_CARD_ID = "assessment-section";

/**
 * `/company/assessment` (login only): the supplier assessment, a section at a time, with the sections its risk tier
 * sends. `?section=` opens one, `?id=` a past assessment read-only. Answers save as the supplier goes; submitting
 * sends it to Pollards' ISO manager and the form becomes read-only. A supplier's own update is a new assessment from
 * its current answers (Ben, 3 Nov).
 */
export function AssessmentPage() {
  const adapter = useSupplierPortalAdapter();
  const [params] = useSearchParams();
  const id = params.get("id");
  const {
    data: assessment,
    error,
    isLoading,
    reload,
  } = useLoad(
    () => adapter.getAssessment(id ?? undefined),
    `assessment:${id ?? "open"}`,
  );

  if (error && !assessment)
    return (
      <LoadFailed
        what="your assessment"
        message={mutationErrorMessage(error, "Check your connection.")}
        onRetry={reload}
      />
    );
  if (isLoading && !assessment) return <LoadingCard what="your assessment" />;
  if (!assessment)
    return (
      <Card>
        <EmptyState
          title="No assessment to complete"
          action={
            <Button asChild variant="outline">
              <Link to={paths.supplierCompany()}>Back to your company</Link>
            </Button>
          }
        >
          Pollards hasn’t asked you for an assessment.
        </EmptyState>
      </Card>
    );
  // A reload that brings newer answers (a save made in another window) starts the form afresh from them.
  return (
    <AssessmentForm
      key={`${assessment.id}:${assessment.state}:${assessment.answers_version}`}
      assessment={assessment}
      onReload={reload}
    />
  );
}

function AssessmentForm({
  assessment,
  onReload,
}: {
  assessment: SupplierAssessment;
  onReload: () => void;
}) {
  const adapter = useSupplierPortalAdapter();
  const capabilities = usePlatformCapabilities();
  const summary = useCompanySummary();
  const [params, setParams] = useSearchParams();
  const [triedToSubmit, setTriedToSubmit] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [serverMissing, setServerMissing] = useState<MissingItem[] | null>(
    null,
  );
  const { request } = assessment;
  const isSubmitted = assessment.read_only;
  const isPast = assessment.state === "accepted";
  const isUpdate = assessment.reason === "update";

  const { answers, setAnswers, status, retry, stop } = useAssessmentAutosave({
    adapter,
    assessmentId: assessment.id,
    initial: {
      answers: assessment.answers,
      version: assessment.answers_version,
    },
    isEnabled: !isSubmitted,
  });
  const files = useMemo(
    () => ({
      upload: (answer: FileAnswer, file: File) =>
        adapter.uploadAssessmentFile(assessment.id, answer, file),
      href: adapter.companyFileHref,
    }),
    [adapter, assessment.id],
  );
  const change = (next: Partial<AssessmentAnswers>) => {
    setServerMissing(null);
    setAnswers(next);
  };

  const steps = stepsFor(request, answers.iso_9001 === "yes");
  const current = steps.find((s) => s.id === params.get("section")) ?? steps[0];
  const index = steps.indexOf(current);
  const missing = serverMissing ?? missingItems(request, answers);
  const missingIn = new Set(missing.map((m) => m.section));

  // Moving to another section starts the reader at its top (not on the first render, nor when the page loads).
  const shown = useRef(current.id);
  useEffect(() => {
    if (shown.current === current.id) return;
    shown.current = current.id;
    capabilities.scrollElementIntoView(SECTION_CARD_ID);
  }, [current.id, capabilities]);

  const go = (section: SectionId) => {
    setParams(
      (previous) => {
        const next = new URLSearchParams(previous);
        next.set("section", section);
        return next;
      },
      { replace: false },
    );
  };

  const submit = async () => {
    setTriedToSubmit(true);
    setSubmitError(null);
    if (missing.length > 0) return;
    setIsSubmitting(true);
    try {
      await adapter.submitAssessment(assessment.id, answers);
      stop();
      summary.refresh();
      onReload();
    } catch (err) {
      const refused = extractFieldErrors(err);
      if (refused.length > 0)
        setServerMissing(
          refused.map((e) => ({
            section: e.field as SectionId,
            text: e.message,
          })),
        );
      else
        setSubmitError(
          mutationErrorMessage(err, "We couldn’t send your assessment."),
        );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="grid gap-4">
      <div className="grid gap-1">
        <div className="flex flex-wrap items-center gap-2.5">
          <h1 className="text-lg font-semibold">
            {isUpdate ? "Your update" : "Supplier assessment"}
          </h1>
          {isPast && assessment.outcome && assessment.accepted_on ? (
            <StatusPill
              label={`${ASSESSMENT_OUTCOME_LABEL[assessment.outcome]} ${day(assessment.accepted_on)}`}
              tone="ok"
            />
          ) : isSubmitted ? (
            <StatusPill label="With Pollards" tone="info" />
          ) : (
            <StatusPill label="To complete" tone="todo" />
          )}
        </div>
        <p className="text-sm text-muted-foreground">
          {isUpdate
            ? `${request.form.label} · started by you on ${day(request.requested_on)}`
            : `${request.form.label}, about ${request.form.minutes} minutes · requested ${request.requested_by ? `by ${request.requested_by} ` : ""}on ${day(request.requested_on)}`}
        </p>
      </div>

      {isPast ? (
        <Banner tone="info">
          <span>
            These are the answers you submitted on{" "}
            {day(assessment.submitted_on ?? request.requested_on)}. They can’t
            be changed: to tell Pollards about a change,{" "}
            <Link
              to={paths.supplierCompany()}
              className="font-medium text-primary underline underline-offset-2"
            >
              update your answers
            </Link>{" "}
            from Your company.
          </span>
        </Banner>
      ) : isSubmitted ? (
        <Banner tone="ok">
          {isUpdate
            ? `You sent your update on ${day(assessment.submitted_on ?? request.requested_on)}. Pollards’ ISO manager is checking the changes; your company stays approved meanwhile.`
            : `You submitted this on ${day(assessment.submitted_on ?? request.requested_on)}. Pollards’ ISO manager is reviewing it and will email you when your company is approved.`}
        </Banner>
      ) : (
        index === 0 &&
        (isUpdate ? (
          <div className="grid gap-1 rounded-lg border border-primary bg-primary/10 px-3.5 py-3 text-sm">
            <strong>
              Your answers of{" "}
              {day(assessment.started_from_on ?? request.requested_on)}, ready
              to change
            </strong>
            <span className="text-muted-foreground">
              Change what’s different, accept the agreement again, then submit
              it. Your company stays approved while Pollards checks the changes.
            </span>
          </div>
        ) : (
          <div className="grid gap-1 rounded-lg border border-primary bg-primary/10 px-3.5 py-3 text-sm">
            <strong>
              {request.form.label}: {steps.length - 1} sections
            </strong>
            <span className="text-muted-foreground">
              {FORM_WHY[request.tier]} Your answers are saved as you go.
            </span>
          </div>
        ))
      )}

      {status === "stale" && (
        <div
          role="alert"
          className="flex flex-wrap items-center gap-3 rounded-lg bg-amber-50 px-3.5 py-3 text-sm dark:bg-amber-950/40"
        >
          <span>
            Your answers were changed in another window or tab, so this one
            can’t save any more.
          </span>
          <Button type="button" size="sm" onClick={onReload}>
            Reload to see the latest
          </Button>
        </div>
      )}

      <div className="grid items-start gap-4 md:grid-cols-[14rem_minmax(0,1fr)]">
        <nav aria-label="Sections">
          <ol className="flex flex-wrap gap-1.5 md:sticky md:top-3 md:flex-col md:gap-0.5">
            {steps.map((s, i) => {
              // Ticked when every answer it asks for is in; flagged only once the supplier has tried to submit (Ben,
              // 3 Nov).
              const isComplete = !missingIn.has(s.id as SectionId);
              const isDone = isComplete && !s.skipped && s.id !== "review";
              const isFlagged = !isComplete && triedToSubmit;
              return (
                <li key={s.id}>
                  <button
                    type="button"
                    aria-current={s.id === current.id ? "step" : undefined}
                    onClick={() => go(s.id)}
                    className={cn(
                      "flex min-h-9 w-full items-center gap-2 rounded-md px-2.5 text-left text-sm max-md:border",
                      s.id === current.id
                        ? "bg-primary/10 font-semibold text-primary"
                        : "hover:bg-muted",
                    )}
                  >
                    <span
                      aria-hidden
                      className={cn(
                        "grid size-[18px] flex-none place-items-center rounded-full border text-[10px]",
                        isDone &&
                          "border-green-700 bg-green-700 text-white dark:border-green-400 dark:bg-green-400 dark:text-green-950",
                        isFlagged && "border-amber-600 text-amber-700",
                      )}
                    >
                      {isDone ? (
                        <Check className="size-3" />
                      ) : isFlagged ? (
                        "!"
                      ) : (
                        i + 1
                      )}
                    </span>
                    <span
                      className={cn(
                        s.skipped && "text-muted-foreground line-through",
                      )}
                    >
                      {s.label}
                    </span>
                  </button>
                </li>
              );
            })}
          </ol>
        </nav>

        <Card id={SECTION_CARD_ID} className="scroll-mt-3 gap-4 p-4 sm:p-5">
          <div className="flex flex-wrap items-start justify-between gap-2">
            <div className="grid gap-0.5">
              <span className="text-[11px] font-bold tracking-wider text-muted-foreground uppercase">
                {current.id === "review"
                  ? "Last step"
                  : `Section ${index + 1} of ${steps.length - 1}`}
              </span>
              <h2 className="text-base font-semibold">{current.label}</h2>
            </div>
            {!isSubmitted && <SaveState status={status} onRetry={retry} />}
          </div>
          {current.id === "review" ? (
            <Review
              missing={missing}
              isSubmitted={isSubmitted}
              triedToSubmit={triedToSubmit}
              onGo={go}
            />
          ) : (
            <AssessmentFilesContext.Provider value={files}>
              <fieldset disabled={isSubmitted} className="grid gap-4">
                <AssessmentSectionBody
                  section={current.id}
                  request={request}
                  answers={answers}
                  agreement={assessment.agreement}
                  set={change}
                />
              </fieldset>
            </AssessmentFilesContext.Provider>
          )}
          {submitError && (
            <p role="alert" className="text-sm text-red-600">
              {submitError}
            </p>
          )}
          <div className="flex flex-wrap items-center justify-between gap-2 border-t pt-3">
            <Button
              type="button"
              variant="outline"
              disabled={index === 0}
              onClick={() => go(steps[index - 1].id)}
            >
              Back
            </Button>
            {current.id !== "review" ? (
              <Button type="button" onClick={() => go(steps[index + 1].id)}>
                {isSubmitted ? "Next" : "Save and continue"}
              </Button>
            ) : (
              !isSubmitted && (
                <Button
                  type="button"
                  disabled={isSubmitting || status === "stale"}
                  onClick={() => void submit()}
                >
                  {isUpdate ? "Send your update" : "Submit your assessment"}
                </Button>
              )
            )}
          </div>
        </Card>
      </div>
    </div>
  );
}

function SaveState({
  status,
  onRetry,
}: {
  status: SaveStatus;
  onRetry: () => void;
}) {
  if (status === "failed")
    return (
      <span role="alert" className="flex items-center gap-2 text-sm">
        <span className="text-red-600">Not saved.</span>
        <Button type="button" variant="outline" size="sm" onClick={onRetry}>
          Try saving again
        </Button>
      </span>
    );
  if (status === "idle" || status === "stale") return null;
  return (
    <span aria-live="polite" className="text-xs text-muted-foreground">
      {status === "saving" ? "Saving…" : "Saved"}
    </span>
  );
}

function Review({
  missing,
  isSubmitted,
  triedToSubmit,
  onGo,
}: {
  missing: MissingItem[];
  isSubmitted: boolean;
  triedToSubmit: boolean;
  onGo: (id: SectionId) => void;
}) {
  if (isSubmitted)
    return (
      <p className="text-sm text-muted-foreground">
        These answers have been submitted. Use the sections to look back over
        them.
      </p>
    );
  if (missing.length === 0)
    return (
      <Banner tone="ok">
        Everything we need is here. Submit it to send it to Pollards’ ISO
        manager.
      </Banner>
    );
  return (
    <div
      role={triedToSubmit ? "alert" : undefined}
      className="grid gap-2 rounded-lg bg-amber-50 px-3.5 py-3 text-sm dark:bg-amber-950/40"
    >
      <strong>
        {missing.length === 1
          ? "One thing is still needed before you can submit"
          : `${missing.length} things are still needed before you can submit`}
      </strong>
      <ul className="grid gap-1 pl-5 [list-style:disc]">
        {missing.map((m) => (
          <li key={m.text}>
            {m.text}{" "}
            <button
              type="button"
              className="font-medium text-primary underline underline-offset-2"
              onClick={() => onGo(m.section)}
            >
              Go to it
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}
